import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import StatCard from "@/components/StatCard";
import SeverityBadge from "@/components/SeverityBadge";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ShieldAlert, Activity, Skull, Timer, ArrowUpRight, Fingerprint, Zap } from "lucide-react";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [compliance, setCompliance] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [s, i, c] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/incidents"),
          api.get("/compliance/summary"),
        ]);
        setSummary(s.data);
        setIncidents(i.data);
        setCompliance(c.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196] flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#34C759] pulse-critical" />
            LIVE · UNIFIED CONSOLE
          </div>
          <h1 className="font-display font-black text-5xl mt-2">Mission Control</h1>
          <p className="text-[#8E9196] mt-1">Single pane of glass across your SOC operations.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/investigations/new" data-testid="dash-new-investigation"
            className="tracing-beam inline-flex items-center gap-2 border border-[#00F0FF] bg-[#00F0FF] text-black hover:bg-[#00F0FF]/80 px-4 py-2.5 rounded-sm font-mono uppercase text-xs tracking-widest">
            <Fingerprint className="w-4 h-4" /> New investigation
          </Link>
          <div className="text-right font-mono text-xs text-[#8E9196] uppercase tracking-widest">
            <div>{new Date().toUTCString().replace("GMT", "UTC")}</div>
            <div className="mt-1">Session: <span className="text-white">healthy</span></div>
          </div>
        </div>
      </header>

      {/* Platform SMTP heartbeat — small strip so operators see mail health at a glance */}
      <PlatformMailHealth />

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          testid="kpi-active-alerts"
          label="Active Alerts (24h)"
          value={summary?.kpis?.active_alerts ?? "—"}
          sub="↑ correlated events"
          accent="#FF9F0A"
          icon={Activity}
        />
        <StatCard
          testid="kpi-open-incidents"
          label="Open Incidents"
          value={summary?.kpis?.open_incidents ?? "—"}
          sub={`${summary?.incidents_total ?? 0} total`}
          accent="#00F0FF"
          icon={ShieldAlert}
        />
        <StatCard
          testid="kpi-threat-score"
          label="Threat Score"
          value={summary?.kpis?.threat_score ?? "—"}
          sub="out of 100"
          accent="#FF3B30"
          icon={Skull}
        />
        <StatCard
          testid="kpi-mttr"
          label="MTTR"
          value={`${summary?.kpis?.mttr_hours ?? "—"}h`}
          sub="mean time to respond"
          accent="#34C759"
          icon={Timer}
        />
      </div>

      {/* Middle Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="alert-timeline">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Alert Volume · 24h</div>
              <div className="font-display text-2xl mt-1">Signals timeline</div>
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <span className="w-2 h-2 bg-[#00F0FF]" /> alerts
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={summary?.timeline || []}>
                <defs>
                  <linearGradient id="alertGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="hour" stroke="#52525B" fontSize={10} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} />
                <YAxis stroke="#52525B" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 11 }}
                  labelStyle={{ color: "#8E9196" }}
                  itemStyle={{ color: "#00F0FF" }}
                />
                <Area type="monotone" dataKey="alerts" stroke="#00F0FF" strokeWidth={1.5} fill="url(#alertGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="top-assets">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Top affected assets</div>
          <div className="font-display text-2xl mt-1 mb-4">Hot spots</div>
          <div className="space-y-3">
            {(summary?.top_assets || []).length === 0 && (
              <div className="text-sm text-[#52525B]">No affected assets.</div>
            )}
            {(summary?.top_assets || []).map((a) => (
              <div key={a.asset} className="flex items-center justify-between border-b border-white/5 pb-2">
                <div className="font-mono text-sm truncate">{a.asset}</div>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-1 bg-white/5">
                    <div className="h-1 bg-[#FF9F0A]" style={{ width: `${Math.min(100, a.incidents * 25)}%` }} />
                  </div>
                  <div className="font-mono text-xs text-white w-6 text-right">{a.incidents}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-white/10 bg-[#121212] rounded-sm" data-testid="recent-incidents">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Recent Incidents</div>
              <div className="font-display text-lg">Active investigations</div>
            </div>
            <Link to="/incidents" className="text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline flex items-center gap-1" data-testid="link-all-incidents">
              View all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-white/5">
            {incidents.slice(0, 5).map((inc) => (
              <Link
                key={inc.id}
                to={`/incidents/${inc.id}`}
                data-testid={`dash-incident-${inc.id}`}
                className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors"
              >
                <SeverityBadge severity={inc.severity} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{inc.title}</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-[#52525B] mt-0.5">
                    {inc.asset || "unknown asset"} · {inc.status}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-[#8E9196] uppercase tracking-widest">
                  {inc.assignee || "unassigned"}
                </div>
              </Link>
            ))}
            {!incidents.length && <div className="px-5 py-8 text-center text-sm text-[#52525B]">No incidents yet.</div>}
          </div>
        </div>

        <div className="border border-white/10 bg-[#121212] rounded-sm p-5" data-testid="compliance-summary">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Compliance posture</div>
          <div className="font-display text-2xl mt-1 mb-4">Frameworks</div>
          <div className="space-y-3">
            {Object.entries(compliance).map(([fw, stats]) => (
              <div key={fw}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-mono uppercase tracking-widest text-[#8E9196]">{fw}</span>
                  <span className="font-mono text-white">{stats.score}%</span>
                </div>
                <div className="h-1 bg-white/5 rounded-sm overflow-hidden flex">
                  <div className="h-full bg-[#34C759]" style={{ width: `${(stats.passing / stats.total) * 100}%` }} />
                  <div className="h-full bg-[#FFCC00]" style={{ width: `${(stats.warning / stats.total) * 100}%` }} />
                  <div className="h-full bg-[#FF3B30]" style={{ width: `${(stats.failing / stats.total) * 100}%` }} />
                </div>
              </div>
            ))}
            {!Object.keys(compliance).length && <div className="text-sm text-[#52525B]">Loading…</div>}
          </div>
          <Link to="/compliance" className="mt-4 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[#00F0FF] hover:underline">
            Open compliance <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}


function PlatformMailHealth() {
  const [h, setH] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/platform/smtp/health");
      setH(data);
    } catch (_e) { /* silent — non-critical */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const bumpNow = async () => {
    setRefreshing(true);
    try { await api.post("/platform/smtp/test").catch(() => {}); }
    finally { await load(); setRefreshing(false); }
  };
  if (!h) return null;
  if (!h.configured) {
    return (
      <div className="mb-6 border border-[#FFCC00]/40 bg-[#FFCC00]/[0.05] rounded-sm px-4 py-2.5 flex items-center gap-3" data-testid="mail-health-widget">
        <span className="w-2 h-2 rounded-full bg-[#FFCC00]" />
        <div className="flex-1 text-xs">
          <span className="font-mono uppercase tracking-widest text-[#FFCC00]">Mail service · not configured</span>
          <span className="text-[#8E9196] ml-2">Set the platform default at <Link to="/platform/smtp" className="text-[#00F0FF] hover:underline">Platform Mail Defaults</Link>.</span>
        </div>
      </div>
    );
  }
  const ok = h.imap_ok && h.smtp_ok;
  const dotColor = ok ? "#34C759" : "#FF3B30";
  return (
    <div className="mb-6 border border-white/10 bg-[#0f0f10] rounded-sm px-4 py-2.5 flex items-center gap-4" data-testid="mail-health-widget">
      <span className="relative w-2 h-2 shrink-0">
        <span className="absolute inset-0 rounded-full" style={{ background: dotColor }} />
        {ok && <span className="absolute inset-0 rounded-full animate-ping" style={{ background: dotColor, opacity: 0.6 }} />}
      </span>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Mail service</div>
      <div className="flex items-center gap-3 text-xs font-mono">
        <span className={h.imap_ok ? "text-[#34C759]" : "text-[#FF3B30]"} title={h.imap_detail}>
          IMAP {h.imap_ok ? "✓" : "✗"}
          <span className="text-[#52525B] normal-case ml-1">{h.imap_host}</span>
        </span>
        <span className="text-[#52525B]">·</span>
        <span className={h.smtp_ok ? "text-[#34C759]" : "text-[#FF3B30]"} title={h.smtp_detail}>
          SMTP {h.smtp_ok ? "✓" : "✗"}
          <span className="text-[#52525B] normal-case ml-1">{h.smtp_host}</span>
        </span>
        <span className="text-[#52525B]">·</span>
        <span className="text-[#8E9196]">from <span className="text-white">{h.from_address}</span></span>
      </div>
      <div className="ml-auto flex items-center gap-3 text-[10px] font-mono text-[#52525B]">
        {h.checked_at && <span>checked {new Date(h.checked_at).toLocaleTimeString()}</span>}
        <button onClick={bumpNow} disabled={refreshing} data-testid="mail-health-recheck"
          className="text-[#00F0FF] hover:text-white disabled:opacity-40 uppercase tracking-widest">
          {refreshing ? "checking…" : "re-check"}
        </button>
      </div>
    </div>
  );
}
